/**
 * V3 — Dashboard. KPIs visuels + graphiques + tableaux récents.
 */
import React, { useEffect, useState } from 'react';
import { getDashboard } from './api';
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend
} from 'recharts';
import { Banknote, Fuel, ShoppingBag, AlertTriangle, TrendingUp, Wallet } from 'lucide-react';

function fmt(n, suffix = '') {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M' + suffix;
  if (Math.abs(v) >= 1_000) return Math.round(v).toLocaleString('fr-FR') + suffix;
  return v.toFixed(2) + suffix;
}

function fmtCurrency(n) {
  if (n === null || n === undefined) return '—';
  return Math.round(Number(n)).toLocaleString('fr-FR') + ' FCFA';
}

export default function V3Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.resolve().then(async () => {
      try {
        const d = await getDashboard();
        setData(d);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    });
  }, []);

  if (err) return <div className="v3-error">⚠️ {err}</div>;
  if (!data) return <div className="v3-skeleton">Chargement…</div>;

  const k = data.kpis;
  return (
    <div className="v3-page" data-testid="v3-dashboard">
      <div className="v3-page-head">
        <h1>Tableau de bord</h1>
        <p>Vue agrégée des 30 derniers jours · Station ROPS</p>
      </div>

      <section className="v3-kpi-grid">
        <KpiCard icon={<Banknote/>} label="CA Piste (30j)" value={fmtCurrency(k.total_ca_30j)} accent="amber"
                 sub={`${k.n_entries_30j} jour${k.n_entries_30j>1?'s':''} saisi${k.n_entries_30j>1?'s':''}`} />
        <KpiCard icon={<Fuel/>} label="Litres Gasoil" value={fmt(k.total_litres_go_30j, ' L')} accent="emerald"
                 sub="Volume vendu sur 30j" />
        <KpiCard icon={<Fuel/>} label="Litres Super" value={fmt(k.total_litres_sp_30j, ' L')} accent="cyan"
                 sub="Volume vendu sur 30j" />
        <KpiCard icon={<ShoppingBag/>} label="CA Shop" value={fmtCurrency(k.total_shop_30j)} accent="violet"
                 sub="Total boutique 30j" />
        <KpiCard icon={<Wallet/>} label="Crédits clients" value={fmtCurrency(k.total_credits_30j)} accent="orange"
                 sub="Encours total 30j" />
        <KpiCard icon={<AlertTriangle/>} label="Écart caisse" value={fmtCurrency(k.total_ecart_30j)} accent={k.total_ecart_30j<0?'rose':'emerald'}
                 sub={`Manquants pompistes : ${fmtCurrency(k.total_manquants_30j)}`} />
        <KpiCard icon={<TrendingUp/>} label="Moyenne jour" value={fmtCurrency(k.avg_ca_per_day)} accent="amber"
                 sub={`Sur ${k.n_entries_30j} jour${k.n_entries_30j>1?'s':''}`} />
      </section>

      <section className="v3-grid-2">
        <div className="v3-card v3-card-chart">
          <div className="v3-card-head">
            <h2>Chiffre d&apos;affaires journalier</h2>
            <span className="v3-card-sub">CA Piste + Shop · 30 derniers jours</span>
          </div>
          <div className="v3-card-body" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily_series}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => fmt(v)} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                  formatter={(value, name) => [fmtCurrency(value), name === 'ca' ? 'CA Piste' : 'Shop']}
                />
                <Line type="monotone" dataKey="ca" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3, fill: '#f59e0b' }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="shop" stroke="#8b5cf6" strokeWidth={1.8} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="v3-card v3-card-chart">
          <div className="v3-card-head">
            <h2>Volume carburant</h2>
            <span className="v3-card-sub">Litres GO vs Super</span>
          </div>
          <div className="v3-card-body" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily_series}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }}
                  formatter={(v, n) => [v.toLocaleString('fr-FR') + ' L', n === 'litres_go' ? 'Gasoil' : 'Super']}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                <Bar dataKey="litres_go" name="Gasoil" fill="#10b981" radius={[3,3,0,0]} />
                <Bar dataKey="litres_sp" name="Super" fill="#06b6d4" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="v3-grid-2">
        <div className="v3-card">
          <div className="v3-card-head">
            <h2>Top crédits clients</h2>
            <span className="v3-card-sub">Encours cumulé 30j</span>
          </div>
          <div className="v3-card-body">
            {data.top_credits.length === 0 && <div className="v3-empty">Aucun crédit en cours.</div>}
            {data.top_credits.map((c) => (
              <div className="v3-list-row" key={c.name}>
                <span className="v3-list-name">{c.name}</span>
                <span className="v3-list-value">{fmtCurrency(c.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="v3-card">
          <div className="v3-card-head">
            <h2>Top pompistes — manquants</h2>
            <span className="v3-card-sub">Cumul observé</span>
          </div>
          <div className="v3-card-body">
            {data.top_pompistes_manquants.length === 0 && <div className="v3-empty">Aucun manquant.</div>}
            {data.top_pompistes_manquants.map((p) => (
              <div className="v3-list-row" key={p.name}>
                <span className="v3-list-name">{p.name}</span>
                <span className="v3-list-value v3-list-value-warn">{fmtCurrency(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent = 'amber' }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
        {sub && <div className="v3-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}
